import React from "react";
import { NavLink } from "react-router-dom";
import "../styles/AIDriven-Safety-solutions.css";

export default function DigitalAI() {
  const technologyCapabilities = [
    {
      number: "01",
      stage: "Engineering Collaboration",
      title: "Digital Twins and Automation",
      description:
        "Through engineering collaboration, GESI explores how process visualization, hazard analysis, scenario testing, workflow improvement, and safer system design can be supported by digital-twin and automation approaches.",
      considerations: [
        "Process and system visualization",
        "Hazard and scenario analysis",
        "Workflow improvement",
        "Human-centred system design",
      ],
      status: "Concept exploration",
      cta: "Discuss a Digital Twin Concept",
      link: "/contact",
    },
    {
      number: "02",
      stage: "Responsible AI",
      title: "AI and Real-Time Safety Intelligence",
      description:
        "Assess where AI-enabled tools may add value to risk identification, learning, workflow support, or decision-making within clear requirements for governance, data, ethics, security, and human oversight.",
      considerations: [
        "Risk identification",
        "Decision and workflow support",
        "Learning and knowledge access",
        "Governance and human oversight",
      ],
      status: "Use-case evaluation",
      cta: "Explore an AI Use Case",
      link: "/contact",
    },
    {
      number: "03",
      stage: "Responsible Adoption",
      title: "Technology Readiness and Validation",
      description:
        "Before scaling a tool, organizations need evidence that it is relevant, usable, secure, accessible, and aligned with the intended safety outcome.",
      supportingText:
        "Global Digital & Immersive Learning Task Force work supports this broader emphasis on staged review and responsible adoption.",
      considerations: [
        "Relevance and usability",
        "Security and accessibility",
        "Evidence and validation",
        "Implementation readiness",
      ],
      status: "Staged review",
      cta: "Explore Validation Support",
      link: "/contact",
    },
  ];

  const systemRequirements = [
    {
      number: "01",
      title: "Governance",
      description:
        "Define ownership, accountability, acceptable use, and decision authority.",
    },
    {
      number: "02",
      title: "Integration",
      description:
        "Connect the technology with existing people, processes, and systems.",
    },
    {
      number: "03",
      title: "Validation",
      description:
        "Confirm that the tool performs reliably in its intended environment.",
    },
    {
      number: "04",
      title: "Usability",
      description:
        "Design around the people expected to understand and use the technology.",
    },
    {
      number: "05",
      title: "Security",
      description:
        "Protect organizational information, system integrity, and user data.",
    },
    {
      number: "06",
      title: "Human Oversight",
      description:
        "Keep people accountable for consequential safety decisions.",
    },
  ];

  return (
    <main className="digital-ai-page">
      {/* PAGE HERO */}
      <section
        className="digital-ai-hero"
        aria-labelledby="digital-ai-hero-title"
      >
        <div className="digital-ai-hero-background" aria-hidden="true">
          <span className="digital-ai-hero-glow digital-ai-glow-left"></span>
          <span className="digital-ai-hero-glow digital-ai-glow-right"></span>

          <span className="digital-ai-orbit digital-ai-orbit-one"></span>
          <span className="digital-ai-orbit digital-ai-orbit-two"></span>
          <span className="digital-ai-orbit digital-ai-orbit-three"></span>

          <span className="digital-ai-data-line digital-ai-data-line-one"></span>
          <span className="digital-ai-data-line digital-ai-data-line-two"></span>
        </div>

        <div className="digital-ai-hero-container">
          {/* <div className="digital-ai-status">
            <span className="digital-ai-status-dot" aria-hidden="true"></span>
            <span>Strategic Exploration and Responsible Evaluation</span>
          </div> */}

          <p className="digital-ai-kicker">
            Digital, AI and Emerging Technology
          </p>

          <h1 id="digital-ai-hero-title" className="digital-ai-hero-title">
            Human-Centric
            <span> Safety Technology.</span>
          </h1>

          <p className="digital-ai-hero-description">
            GESI explores how digital, AI, automation, and immersive
            technologies can support stronger prevention, better
            decision-making, and safer system design.
          </p>

          <p className="digital-ai-hero-supporting">
            Current work is focused on strategic collaboration, technical
            exploration, and the responsible evaluation of emerging
            technologies rather than offering a broad portfolio of established
            digital pilots.
          </p>

          <div className="digital-ai-collaboration-note">
            <div
              className="digital-ai-collaboration-icon"
              aria-hidden="true"
            >
              iF
            </div>

            <p>
              GESI’s current digital-twin pilot work is being advanced through
              its collaboration with <strong>inFacto Engineering</strong>,
              with a focus on human-centred design, engineering validation,
              system integration, and practical workplace application.
            </p>
          </div>

          <a
            href="/partnerships#infacto-engineering"
            className="digital-ai-primary-button"
          >
            <span>Explore the inFacto Engineering Collaboration</span>

            <span
              className="digital-ai-button-arrow"
              aria-hidden="true"
            >
              ↗
            </span>
          </a>

          <div
            className="digital-ai-hero-path"
            aria-label="Responsible technology progression"
          >
            <span>Explore</span>
            <span aria-hidden="true">→</span>
            <span>Evaluate</span>
            <span aria-hidden="true">→</span>
            <span>Validate</span>
            <span aria-hidden="true">→</span>
            <span>Integrate</span>
          </div>
        </div>
      </section>

      {/* INTRODUCTION */}
      <section
        className="digital-ai-introduction"
        aria-labelledby="digital-ai-introduction-title"
      >
        <div className="digital-ai-introduction-container">
          <div className="digital-ai-introduction-heading">
            <p className="digital-ai-section-kicker">
              Responsible Digital Transformation
            </p>

            <h2
              id="digital-ai-introduction-title"
              className="digital-ai-section-title"
            >
              Technology Must Serve
              <span> the Safety System</span>
            </h2>
          </div>

          <div className="digital-ai-introduction-copy">
            <p>
              Digital transformation can improve visibility, learning, and
              operational control, but technology alone does not create safer
              work.
            </p>

            <p>
              Its value depends on governance, integration, validation,
              usability, security, human oversight, and implementation
              readiness.
            </p>
          </div>

          <div className="digital-ai-requirements">
            {systemRequirements.map((requirement) => (
              <article
                className="digital-ai-requirement"
                key={requirement.title}
              >
                <span className="digital-ai-requirement-number">
                  {requirement.number}
                </span>

                <h3>{requirement.title}</h3>
                <p>{requirement.description}</p>

                <span
                  className="digital-ai-requirement-line"
                  aria-hidden="true"
                ></span>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* TECHNOLOGY CAPABILITIES */}
      <section
        className="digital-ai-capabilities"
        aria-labelledby="digital-ai-capabilities-title"
      >
        <div
          className="digital-ai-capabilities-background"
          aria-hidden="true"
        ></div>

        <div className="digital-ai-capabilities-container">
          <header className="digital-ai-capabilities-heading">
            <p className="digital-ai-section-kicker">
              Current Areas of Exploration
            </p>

            <h2
              id="digital-ai-capabilities-title"
              className="digital-ai-section-title"
            >
              From Technical Possibility to
              <span> Practical Safety Value</span>
            </h2>

            <p className="digital-ai-capabilities-intro">
              GESI approaches emerging technology as a staged process that
              connects organizational need, engineering evidence, responsible
              governance, and practical workplace application.
            </p>
          </header>

          <div className="digital-ai-capability-list">
            <span
              className="digital-ai-capability-track"
              aria-hidden="true"
            ></span>

            {technologyCapabilities.map((capability) => (
              <article
                className="digital-ai-capability"
                key={capability.title}
              >
                <div className="digital-ai-capability-marker">
                  <span>{capability.number}</span>
                </div>

                <div className="digital-ai-capability-card">
                  <div className="digital-ai-capability-top">
                    <div>
                      <div className="digital-ai-capability-meta">
                        <p>{capability.stage}</p>

                        <span className="digital-ai-capability-status">
                          <span aria-hidden="true"></span>
                          {capability.status}
                        </span>
                      </div>

                      <h3>{capability.title}</h3>
                    </div>

                    <span
                      className="digital-ai-capability-index"
                      aria-hidden="true"
                    >
                      {capability.number}
                    </span>
                  </div>

                  <div className="digital-ai-capability-body">
                    <div className="digital-ai-capability-copy">
                      <p className="digital-ai-capability-lead">
                        {capability.description}
                      </p>

                      {capability.supportingText && (
                        <p>{capability.supportingText}</p>
                      )}

                      <div className="digital-ai-capability-principle">
                        <span aria-hidden="true">✓</span>

                        <p>
                          Technology is evaluated as part of a wider safety
                          system—not as a standalone solution.
                        </p>
                      </div>
                    </div>

                    <div className="digital-ai-capability-side">
                      <p className="digital-ai-capability-label">
                        Areas of consideration
                      </p>

                      <ul className="digital-ai-capability-points">
                        {capability.considerations.map((consideration) => (
                          <li key={consideration}>
                            <span aria-hidden="true"></span>
                            {consideration}
                          </li>
                        ))}
                      </ul>

                      <NavLink
                        to={capability.link}
                        className="digital-ai-capability-button"
                      >
                        <span>{capability.cta}</span>

                        <span
                          className="digital-ai-capability-button-arrow"
                          aria-hidden="true"
                        >
                          ↗
                        </span>
                      </NavLink>
                    </div>
                  </div>

                  <span
                    className="digital-ai-capability-bottom-line"
                    aria-hidden="true"
                  ></span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* RESPONSIBLE ADOPTION MODEL */}
      <section
        className="digital-ai-model"
        aria-labelledby="digital-ai-model-title"
      >
        <div className="digital-ai-model-background" aria-hidden="true">
          <span className="digital-ai-model-glow"></span>
        </div>

        <div className="digital-ai-model-container">
          <div className="digital-ai-model-heading">
            <p className="digital-ai-section-kicker">
              Responsible Adoption
            </p>

            <h2
              id="digital-ai-model-title"
              className="digital-ai-section-title"
            >
              Evaluate Before
              <span> You Scale</span>
            </h2>

            <p className="digital-ai-model-description">
              Emerging technology should move through deliberate stages before
              it becomes part of a safety-critical workflow.
            </p>
          </div>

          <div
            className="digital-ai-model-flow"
            aria-label="Responsible technology adoption process"
          >
            <article className="digital-ai-model-step">
              <span>01</span>
              <strong>Define the Need</strong>
              <p>Clarify the problem, users, environment, and desired outcome.</p>
            </article>

            <span className="digital-ai-model-arrow" aria-hidden="true">
              →
            </span>

            <article className="digital-ai-model-step">
              <span>02</span>
              <strong>Explore the Concept</strong>
              <p>Assess technical options, assumptions, and system fit.</p>
            </article>

            <span className="digital-ai-model-arrow" aria-hidden="true">
              →
            </span>

            <article className="digital-ai-model-step">
              <span>03</span>
              <strong>Validate the Approach</strong>
              <p>Test relevance, reliability, usability, and safety value.</p>
            </article>

            <span className="digital-ai-model-arrow" aria-hidden="true">
              →
            </span>

            <article className="digital-ai-model-step">
              <span>04</span>
              <strong>Integrate Responsibly</strong>
              <p>Connect governance, people, systems, and oversight.</p>
            </article>
          </div>
        </div>
      </section>

      {/* PARTNERSHIP FEATURE */}
      <section
        id="infacto-collaboration-preview"
        className="digital-ai-partnership"
        aria-labelledby="digital-ai-partnership-title"
      >
        <div className="digital-ai-partnership-background" aria-hidden="true">
          <span className="digital-ai-partnership-orbit"></span>
          <span className="digital-ai-partnership-glow"></span>
        </div>

        <div className="digital-ai-partnership-container">
          <div className="digital-ai-partnership-mark" aria-hidden="true">
            <span>GESI</span>
            <strong>×</strong>
            <span>inFacto</span>
          </div>

          <div className="digital-ai-partnership-content">
            <p className="digital-ai-section-kicker">
              Engineering Collaboration
            </p>

            <h2
              id="digital-ai-partnership-title"
              className="digital-ai-partnership-title"
            >
              Human-Centred Digital-Twin
              <span> Exploration</span>
            </h2>

            <p>
              GESI and inFacto Engineering are exploring how digital-twin
              approaches can support engineering validation, system
              integration, hazard analysis, and practical workplace
              application.
            </p>

            <a
              href="/partnerships#infacto-engineering"
              className="digital-ai-secondary-button"
            >
              <span>Explore the inFacto Engineering Collaboration</span>

              <span
                className="digital-ai-secondary-button-arrow"
                aria-hidden="true"
              >
                ↗
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section
        className="digital-ai-final"
        aria-labelledby="digital-ai-final-title"
      >
        <div className="digital-ai-final-background" aria-hidden="true">
          <span className="digital-ai-final-glow"></span>
          <span className="digital-ai-final-orbit"></span>
        </div>

        <div className="digital-ai-final-container">
          <p className="digital-ai-section-kicker">
            Explore a Responsible Use Case
          </p>

          <h2
            id="digital-ai-final-title"
            className="digital-ai-final-title"
          >
            Start with the Safety Need,
            <span> Not the Technology.</span>
          </h2>

          <p className="digital-ai-final-description">
            Share the operational challenge, system opportunity, or emerging
            technology concept your organization is considering. GESI can help
            frame the use case, requirements, and responsible path forward.
          </p>

          <NavLink
            to="/contact"
            className="digital-ai-primary-button"
          >
            <span>Start a Conversation</span>

            <span
              className="digital-ai-button-arrow"
              aria-hidden="true"
            >
              ↗
            </span>
          </NavLink>
        </div>
      </section>
    </main>
  );
}